import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Checkbox,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Flag as FlagIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  PendingActions as PendingIcon,
  RateReview as ReviewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';

interface QuestionReport {
  id: number;
  question_id: number;
  question_text: string;
  subject_name: string;
  difficulty: string;
  report_count: number;
  student_name?: string;
  parent_name?: string;
  report_reason: string;
  report_description?: string;
  reported_at: string;
  status: 'pending' | 'under_review' | 'resolved' | 'rejected';
  admin_notes?: string;
  reviewed_by_name?: string;
  reviewed_at?: string;
}

interface ReportStatistics {
  statistics: {
    total_reports: number;
    pending_reports: number;
    under_review_reports: number;
    resolved_reports: number;
    rejected_reports: number;
  };
  by_reason: Array<{ report_reason: string; count: number }>;
  top_reported_questions: Array<any>;
}

const QuestionReports: React.FC = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState<QuestionReport[]>([]);
  const [statistics, setStatistics] = useState<ReportStatistics | null>(null);
  const [selectedReports, setSelectedReports] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalReports, setTotalReports] = useState(0);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Update dialog
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<QuestionReport | null>(null);
  const [updateStatus, setUpdateStatus] = useState<string>('');
  const [updateNotes, setUpdateNotes] = useState<string>('');
  const [updating, setUpdating] = useState(false);

  // Bulk update dialog
  const [bulkUpdateDialogOpen, setBulkUpdateDialogOpen] = useState(false);
  const [bulkStatus, setBulkStatus] = useState<string>('');
  const [bulkNotes, setBulkNotes] = useState<string>('');

  useEffect(() => {
    loadReports();
    loadStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, statusFilter]);

  const loadReports = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: page + 1,
        limit: rowsPerPage,
      };

      if (statusFilter) params.status = statusFilter;

      const response = await api.questionReports.getAll(params);
      setReports(response.data.reports);
      setTotalReports(response.data.pagination.total);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await api.questionReports.getStatistics();
      setStatistics(response.data);
    } catch (err) {
      console.error('Failed to load statistics:', err);
    }
  };

  const handleUpdateReport = (report: QuestionReport) => {
    setSelectedReport(report);
    setUpdateStatus(report.status);
    setUpdateNotes(report.admin_notes || '');
    setUpdateDialogOpen(true);
  };

  const handleSubmitUpdate = async () => {
    if (!selectedReport) return;

    try {
      setUpdating(true);
      await api.questionReports.updateStatus(
        selectedReport.id,
        updateStatus,
        updateNotes || undefined
      );
      setUpdateDialogOpen(false);
      loadReports();
      loadStatistics();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update report');
    } finally {
      setUpdating(false);
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedReports.length === 0) return;

    try {
      setUpdating(true);
      await api.questionReports.bulkUpdate(
        selectedReports,
        bulkStatus,
        bulkNotes || undefined
      );
      setBulkUpdateDialogOpen(false);
      setSelectedReports([]);
      loadReports();
      loadStatistics();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to bulk update reports');
    } finally {
      setUpdating(false);
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedReports(reports.map((r) => r.id));
    } else {
      setSelectedReports([]);
    }
  };

  const handleSelectReport = (reportId: number) => {
    setSelectedReports((prev) =>
      prev.includes(reportId)
        ? prev.filter((id) => id !== reportId)
        : [...prev, reportId]
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'under_review':
        return 'info';
      case 'resolved':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string): React.ReactElement | undefined => {
    switch (status) {
      case 'pending':
        return <PendingIcon fontSize="small" />;
      case 'under_review':
        return <ReviewIcon fontSize="small" />;
      case 'resolved':
        return <CheckCircleIcon fontSize="small" />;
      case 'rejected':
        return <CancelIcon fontSize="small" />;
      default:
        return undefined;
    }
  };

  const getReasonLabel = (reason: string) => {
    const labels: Record<string, string> = {
      wrong_answer: 'Wrong Answer',
      typo: 'Typo',
      incorrect_options: 'Incorrect Options',
      unclear_question: 'Unclear Question',
      missing_information: 'Missing Info',
      other: 'Other',
    };
    return labels[reason] || reason;
  };

  const handleRowClick = (questionId: number) => {
    navigate(`/questions/${questionId}`);
  };

  if (loading && reports.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Question Reports
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
        Manage and review question reports submitted by students and parents
      </Typography>

      {/* Statistics Cards */}
      {statistics && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      Total Reports
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {statistics.statistics.total_reports}
                    </Typography>
                  </Box>
                  <FlagIcon fontSize="large" color="action" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      Pending
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="warning.main">
                      {statistics.statistics.pending_reports}
                    </Typography>
                  </Box>
                  <PendingIcon fontSize="large" color="warning" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      Under Review
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="info.main">
                      {statistics.statistics.under_review_reports}
                    </Typography>
                  </Box>
                  <ReviewIcon fontSize="large" color="info" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      Resolved
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="success.main">
                      {statistics.statistics.resolved_reports}
                    </Typography>
                  </Box>
                  <CheckCircleIcon fontSize="large" color="success" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      Rejected
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="error.main">
                      {statistics.statistics.rejected_reports}
                    </Typography>
                  </Box>
                  <CancelIcon fontSize="large" color="error" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters and Actions */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="under_review">Under Review</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>

          {selectedReports.length > 0 && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setBulkUpdateDialogOpen(true)}
            >
              Bulk Update ({selectedReports.length})
            </Button>
          )}
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Reports Table */}
      <TableContainer component={Paper} elevation={2}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedReports.length === reports.length && reports.length > 0}
                  indeterminate={
                    selectedReports.length > 0 && selectedReports.length < reports.length
                  }
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>Question</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>Reported By</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Reported At</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reports.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No reports found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              reports.map((report) => (
                <TableRow
                  key={report.id}
                  hover
                  onClick={() => handleRowClick(report.question_id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedReports.includes(report.id)}
                      onChange={() => handleSelectReport(report.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={report.question_text}>
                      <Typography variant="body2" sx={{ maxWidth: 300 }} noWrap>
                        {report.question_text}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{report.subject_name}</TableCell>
                  <TableCell>{report.student_name || report.parent_name || 'Unknown'}</TableCell>
                  <TableCell>
                    <Chip label={getReasonLabel(report.report_reason)} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(report.status)}
                      label={report.status.replace('_', ' ')}
                      color={getStatusColor(report.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(report.reported_at).toLocaleDateString('en-IN')}
                  </TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleUpdateReport(report)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalReports}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      {/* Update Report Dialog */}
      <Dialog open={updateDialogOpen} onClose={() => setUpdateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Report Status</DialogTitle>
        <DialogContent>
          {selectedReport && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Question
              </Typography>
              <Typography variant="body1" gutterBottom>
                {selectedReport.question_text}
              </Typography>

              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                Report Reason
              </Typography>
              <Typography variant="body1" gutterBottom>
                {getReasonLabel(selectedReport.report_reason)}
              </Typography>

              {selectedReport.report_description && (
                <>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                    Description
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedReport.report_description}
                  </Typography>
                </>
              )}

              <FormControl fullWidth sx={{ mt: 3 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={updateStatus}
                  label="Status"
                  onChange={(e) => setUpdateStatus(e.target.value)}
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="under_review">Under Review</MenuItem>
                  <MenuItem value="resolved">Resolved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Admin Notes"
                multiline
                rows={4}
                value={updateNotes}
                onChange={(e) => setUpdateNotes(e.target.value)}
                sx={{ mt: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubmitUpdate}
            variant="contained"
            disabled={updating}
          >
            {updating ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Update Dialog */}
      <Dialog open={bulkUpdateDialogOpen} onClose={() => setBulkUpdateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Update Reports</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" gutterBottom>
              Update {selectedReports.length} selected report(s)
            </Typography>

            <FormControl fullWidth sx={{ mt: 3 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={bulkStatus}
                label="Status"
                onChange={(e) => setBulkStatus(e.target.value)}
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="under_review">Under Review</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Admin Notes (Optional)"
              multiline
              rows={4}
              value={bulkNotes}
              onChange={(e) => setBulkNotes(e.target.value)}
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkUpdateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleBulkUpdate}
            variant="contained"
            disabled={updating || !bulkStatus}
          >
            {updating ? 'Updating...' : 'Update All'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuestionReports;
